// Client-side .docx reference extraction. The Word file is an IMPORT SOURCE only —
// extracted metadata is fed into CiteQR's citation engine, which produces the final
// formatted citation in the user's chosen style. Parsing runs entirely in the browser
// (no upload / no retention); mammoth reads text only and never executes macros.
import mammoth from "mammoth/mammoth.browser";
import { looksLikeOrg, looksLikeOrgName, isAcronymOrg } from "@/lib/authors";

const EMPTY = {
  source_type: "journal", work_type: "doctoral", authors: "", editors: "", editors_raw: "", container: "",
  year: "", month: "", day: "", title: "", source: "",
  volume: "", issue: "", pages: "", publisher: "", report_number: "", institution: "", doi: "", url: "", note: "",
};

const REF_HEADINGS = /^\s*\d*\.?\s*(references|reference list|bibliography|works cited|literature cited)\s*:?\s*$/i;
const STOP_HEADINGS = /^\s*\d*\.?\s*(appendix|appendices|acknowledg|about the author|author (bio|note)|figure|table|supplementary|notes)\b/i;

function collapse(s) {
  return (s || "").replace(/\s+/g, " ").trim();
}
function stripTrailingPunct(s) {
  return s.replace(/[.,;]+$/, "");
}

// Normalize an APA parenthetical date's INNER text (parens already removed):
//   "2025" -> {year:"2025"}   "2025, August" -> {year, month}
//   "2025, August 29" -> {year, month, day}   "n.d." -> {year:"n.d."}   "2025a" -> {year:"2025"}
function normalizeApaDate(inner) {
  const s = collapse(inner).replace(/\.$/, "");
  if (/^n\.?\s*d\.?$/i.test(s)) return { year: "n.d.", month: "", day: "" };
  const y = s.match(/^(\d{4})[a-z]?/);
  const year = y ? y[1] : "";
  let month = "", day = "";
  const rest = s.replace(/^\d{4}[a-z]?\s*,?\s*/, "").trim();
  if (rest) {
    const md = rest.match(/^([A-Za-z]+)\.?\s*(\d{1,2})?/);
    if (md) { month = md[1]; day = md[2] || ""; }
  }
  return { year, month, day };
}

// Extract raw text from a .docx array buffer.
export async function extractDocxText(arrayBuffer) {
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value || "";
}

// Locate the references section; fall back to the whole document.
function isolateReferenceSection(text) {
  const lines = text.split(/\r?\n/);
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    if (REF_HEADINGS.test(lines[i].trim())) { start = i + 1; break; }
  }
  if (start === -1) return { section: text, sectionFound: false };
  let end = lines.length;
  for (let i = start; i < lines.length; i++) {
    if (STOP_HEADINGS.test(lines[i].trim())) { end = i; break; }
  }
  return { section: lines.slice(start, end).join("\n"), sectionFound: true };
}

// A plausible author-list OPENING (used to validate a reference boundary before locking it).
function looksLikeAuthorList(s) {
  const t = collapse(s).replace(/^\[?\(?\d{1,3}[\].)]\s+/, ""); // ignore a leading list number
  if (!t || t.length > 700) return false;
  if (/^[A-Z][A-Za-z'’-]+,\s+[A-Z]/.test(t)) return true;                                  // Surname, X
  if (/^(?:van|von|de|del|della|da|di|du|le|la|el|al|bin|ibn|ter|ten)\s+[A-Z]/i.test(t)) return true; // particles
  const head = t.split(/\.\s|\(/)[0];
  if (isAcronymOrg(head) || looksLikeOrgName(head) || looksLikeOrg(t)) return true;         // orgs/acronyms
  return false;
}

// For a "(year)" found at `yearIdx`, locate where its AUTHOR list begins: immediately after the
// PREVIOUS reference's terminator — a URL, a DOI, or a true sentence end — but NEVER an author
// initial (e.g. "J.") or acronym dot. Returns null if the text before the year is not an author list
// (so a "(year)" appearing inside a title never creates a false boundary).
function findAuthorStart(text, yearIdx) {
  const region = text.slice(0, yearIdx);
  const termRe = /(?:https?:\/\/\S+|\b10\.\d{4,9}\/\S+|[a-z0-9)\]][.!?])\s+/g;
  const positions = [];
  let mm;
  while ((mm = termRe.exec(region))) positions.push(mm.index + mm[0].length);
  // Try each terminator from the one CLOSEST to the year backward (then the very start), and take
  // the first whose following text is a valid author list. This skips an org author's OWN trailing
  // period (e.g. "…Administration. (2020)") and lands on the real author-list start.
  const cands = positions.reverse();
  cands.push(0);
  for (const pos of cands) {
    if (pos >= yearIdx) continue;
    if (looksLikeAuthorList(text.slice(pos, yearIdx))) return pos;
  }
  return null;
}

// STEP 1 — lock reference boundaries FIRST. Each reference is "Author-list (Year). …", so we anchor
// on every "author-list (year)" opener and slice the section into isolated raw-reference blocks.
// Word line-wrapping/page breaks (joined here into one stream) can never split a reference, and one
// reference's text can never bleed into the previous or next record. Field extraction happens later.
function splitReferences(section) {
  const text = collapse(section.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).join(" "));
  if (!text) return [];
  const yearRe = /\(\s*(?:n\.?\s*d\.?|(?:1[6-9]|20)\d{2}[a-z]?)(?:\s*,[^)]*)?\)/gi;
  const bounds = [];
  let m;
  while ((m = yearRe.exec(text))) {
    const start = findAuthorStart(text, m.index);
    if (start != null && (!bounds.length || start > bounds[bounds.length - 1])) bounds.push(start);
  }
  if (!bounds.length) return [text];
  if (bounds[0] !== 0) bounds.unshift(0);
  const refs = [];
  for (let i = 0; i < bounds.length; i++) {
    const seg = collapse(text.slice(bounds[i], i + 1 < bounds.length ? bounds[i + 1] : text.length));
    if (seg) refs.push(seg);
  }
  return refs;
}

// Convert an APA-style author string to CiteQR's "; "-separated raw form.
function fixAuthors(a) {
  const raw = collapse(a);
  // Keep organization/group names intact (never split "…Control and Prevention"); just drop a trailing period.
  if (looksLikeOrg(raw)) return raw.replace(/\.\s*$/, "");
  let s = raw
    .replace(/\s*(?:…|\.\s?\.\s?\.)\s*/g, "; ")   // APA 21+ author ellipsis -> separator
    .replace(/,?\s*&\s*/g, "; ")
    .replace(/\s+and\s+/gi, "; ");
  if (/^[A-Z]\.\s/.test(s)) {
    // Forward order ("F. Surname, G. Surname2, & H. Surname3") — typical for chapter editors.
    s = s.replace(/([\p{L}’'.-]{2,})\s*,\s*(?=[A-Z]\.)/gu, "$1; ");
  } else {
    // Inverted order ("Grohol, J. M., Smith, A. B.") — insert ";" between an initial and next surname.
    s = s.replace(/([A-Z]\.)\s*,\s*(?=[\p{Lu}][\p{L}'’.-]+,\s*[\p{Lu}])/gu, "$1; ");
  }
  s = s.replace(/,\s*;/g, "; ").replace(/;\s*;/g, "; ").replace(/^;\s*|;\s*$/g, "").trim();
  // Drop any stray trailing comma left on an individual author (e.g. from the ellipsis boundary).
  return s.split(/;\s*/).map((x) => x.replace(/,\s*$/, "").trim()).filter(Boolean).join("; ");
}

// Normalize any DOI form (bare, doi:, dx.doi.org, http://doi.org) to https://doi.org/….
function normalizeDoi(bare) {
  const d = collapse(bare)
    .replace(/^doi:\s*/i, "")
    .replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
  return d ? `https://doi.org/${stripTrailingPunct(d)}` : "";
}

// Does a segment look like a personal/group AUTHOR (vs. a page title)? Used to avoid shifting an
// authorless webpage's title into the author slot.
function looksLikeAuthorSegment(seg) {
  const s = collapse(seg);
  if (!s) return false;
  if (looksLikeOrgName(s)) return true;                        // real organization (not a sentence-case title)
  if (/;/.test(s)) return true;                                // multiple authors
  if (/[A-Z][A-Za-z'’-]+,\s*[A-Z]\.?/.test(s)) return true;    // "Surname, X." / "Surname, X. Y."
  if (/,/.test(s) && /\b(&|and)\b/i.test(s)) return true;      // "Smith, A., & Doe, B."
  return false;
}

function detectSourceType(text, ref) {
  const t = text.toLowerCase();
  if (/\b(doctoral dissertation|master'?s thesis|ph\.?d\.? (thesis|dissertation)|thesis)\b/.test(t)) return "dissertation";
  if (/\bproceedings\b|\bconference\b|\bsymposium\b|\bpaper presented\b|\bposter (session|presentation)\b/.test(t)) return "conference";
  if (/\bin\b.+\(eds?\.\)|\bin\b.+\(ed\.\)/.test(t)) return "chapter";
  if (ref.volume || (ref.source && ref.pages)) return "journal";
  if (ref.url && !ref.source) return "website";
  if (ref.publisher && !ref.volume) return "book";
  return "journal";
}

// Best-effort structured parse of a single free-text reference.
function parseFreeform(raw) {
  const ref = { ...EMPTY };
  const flags = { needsReview: false };
  let s = collapse(raw).replace(/^\[?\(?\d{1,3}[\].)]\s+/, ""); // strip leading number

  const doiM = s.match(/\b10\.\d{4,9}\/[^\s"'<>]+/i);
  if (doiM) ref.doi = normalizeDoi(doiM[0]); // canonical https://doi.org/…
  const urlM = s.match(/https?:\/\/[^\s"'<>]+/i);
  if (urlM && !/doi\.org/i.test(urlM[0])) ref.url = stripTrailingPunct(urlM[0]);
  const pmidM = s.match(/PMID:?\s*(\d{1,9})/i);
  if (pmidM) ref._pmid = pmidM[1];

  // Working copy without the trailing DOI/URL noise for text parsing.
  let work = s.replace(/https?:\/\/[^\s"'<>]+/gi, "").replace(/\bdoi:\s*\S+/gi, "").replace(/PMID:?\s*\d+/gi, "").trim();

  // APA parenthetical date: (2025), (2025, August), (2025, August 29), (n.d.), (2025a).
  // The WHOLE parenthetical is consumed so it never leaks into author/title.
  const dateM = work.match(/\(\s*(n\.?\s*d\.?|\d{4}[a-z]?(?:\s*,\s*[^)]*)?)\s*\)/i);
  let hasParenDate = false;
  if (dateM) {
    const nd = normalizeApaDate(dateM[1]);
    ref.year = nd.year;
    ref.month = nd.month;
    ref.day = nd.day;
    hasParenDate = true;
  }
  const yearBare = work.match(/\b(19|20)\d{2}[a-z]?\b/);
  if (!hasParenDate && yearBare) ref.year = yearBare[0].replace(/[a-z]$/i, "");

  if (hasParenDate) {
    const idx = work.indexOf(dateM[0]);
    const beforeDate = collapse(work.slice(0, idx));
    let after = collapse(work.slice(idx + dateM[0].length).replace(/^[.\s]+/, ""));

    // Lift a report/publication number out of "(Report No. X)", "(Publication No. X)", or an
    // agency-prefixed "(HHS Publication No. X)" so it is stored in its own field and never merged
    // into the title or publisher.
    let reportNo = "";
    const repNoM = after.match(/\([^)]*\b(?:report|publication|technical report|working paper|contract|grant)\b[^)]*?(?:no\.?|number|#)\s*[:\-]?\s*([A-Za-z0-9][A-Za-z0-9.\-/]*)\s*\)/i);
    if (repNoM) {
      reportNo = collapse(repNoM[1]);
      after = collapse(after.replace(repNoM[0], " "));
    }

    const hasAuthor = looksLikeAuthorSegment(beforeDate);

    // 1) Dissertation / thesis: "[Doctoral dissertation, Institution]" / "[Master's thesis, Institution]",
    //    ProQuest Dissertations & Theses, etc. Fields (title, type, institution, repository, DOI/URL) stay separate.
    const dissM = after.match(/\[\s*(unpublished\s+)?(doctoral (?:dissertation|thesis)|master'?s (?:thesis|dissertation)|ph\.?\s*d\.?\s+(?:thesis|dissertation))\s*(?:,\s*([^\]]*))?\]/i);
    const dissText = /\b(doctoral dissertation|master'?s thesis|ph\.?d\.? (?:thesis|dissertation)|proquest dissertations)\b/i.test(after);

    // Chapter in an edited book: "Chapter title. In A. Editor & B. Editor (Eds.), Book title (pp. x–y). Publisher."
    const chapM = after.match(/^(.*?)\.\s*In\s+(.+?)\s*\(Eds?\.\),?\s*(.*)$/i);

    // Conference paper / poster: "Title [Paper presentation]. Conference Name, City, State, Country."
    const confM = after.match(/\[\s*(paper presentation|poster (?:session|presentation)|conference session|keynote address)\s*\]/i);

    if (dissM || dissText) {
      ref.source_type = "dissertation";
      ref.authors = fixAuthors(beforeDate);
      if (reportNo) ref.report_number = reportNo; // published dissertations carry a Publication No.
      ref.work_type = /master/i.test((dissM && dissM[2]) || after) ? "masters" : "doctoral";
      if (dissM) {
        const bIdx = after.indexOf(dissM[0]);
        ref.title = stripTrailingPunct(collapse(after.slice(0, bIdx)));
        if (dissM[3]) ref.institution = stripTrailingPunct(collapse(dissM[3]));
        let tail = collapse(after.slice(bIdx + dissM[0].length).replace(/^[.\s]+/, ""))
          .replace(/\b(retrieved|available)\s+(from|at)\b.*$/i, "").trim();
        if (tail) ref.source = stripTrailingPunct(tail.split(/\.\s+/)[0]); // repository / database name
      } else {
        const parts = after.split(/\.\s+/);
        ref.title = stripTrailingPunct(collapse(parts.shift() || ""));
        let tail = collapse(parts.join(". ")).replace(/\b(retrieved|available)\s+(from|at)\b.*$/i, "").trim();
        const instM = tail.match(/\b(?:doctoral dissertation|master'?s thesis)[,\s]+(.+)$/i);
        if (instM) ref.institution = stripTrailingPunct(collapse(instM[1]).replace(/\.$/, ""));
        else if (tail) ref.source = stripTrailingPunct(tail);
      }
    } else if (confM) {
      // Conference paper/poster presentation.
      ref.source_type = "conference";
      ref.authors = fixAuthors(beforeDate);
      ref.conf_type = /poster/i.test(confM[1]) ? "poster" : "paper";
      const bIdx = after.indexOf(confM[0]);
      ref.title = stripTrailingPunct(collapse(after.slice(0, bIdx)));
      if (ref.month) ref.event_date = collapse(`${ref.month} ${ref.day}`);
      const tail = collapse(after.slice(bIdx + confM[0].length).replace(/^[.\s]+/, ""));
      const sentence = tail.split(/\.\s+/)[0];
      const commaParts = sentence.split(/\s*,\s*/).filter(Boolean);
      if (commaParts.length >= 2) {
        ref.conference = stripTrailingPunct(commaParts[0]);
        ref.location = stripTrailingPunct(commaParts.slice(1).join(", "));
      } else {
        ref.conference = stripTrailingPunct(sentence);
      }
    } else if (chapM) {
      ref.authors = fixAuthors(beforeDate);
      ref.source_type = "chapter";
      ref.title = stripTrailingPunct(collapse(chapM[1]));
      ref.editors = fixAuthors(chapM[2]);
      ref.editors_raw = ref.editors;
      const tail = collapse(chapM[3]); // "Book title (pp. 115–132). Publisher."
      const pgM = tail.match(/\(pp?\.\s*([\dA-Za-z]+\s*[-–]\s*[\dA-Za-z]+|\d+)\)/i);
      if (pgM) {
        ref.pages = pgM[1].replace(/\s*[-–]\s*/, "–").trim();
        ref.container = stripTrailingPunct(collapse(tail.slice(0, tail.indexOf(pgM[0]))));
        const afterPg = collapse(tail.slice(tail.indexOf(pgM[0]) + pgM[0].length).replace(/^[.\s]+/, ""));
        if (afterPg) ref.publisher = stripTrailingPunct(afterPg);
      } else {
        const segs = tail.split(/\.\s+/);
        ref.container = stripTrailingPunct(collapse(segs.shift() || ""));
        ref.publisher = stripTrailingPunct(collapse(segs.join(". ")));
      }
    } else if (!hasAuthor && ref.url && !reportNo && !/\breport\b/i.test(after)) {
      // 2) Webpage with NO personal/group author: leading text is the PAGE TITLE and the post-date
      //    text is the WEBSITE / publisher name — never shift the title into the author slot.
      ref.source_type = "website";
      ref.authors = "";
      ref.title = stripTrailingPunct(beforeDate);
      const site = collapse(after).replace(/\b(retrieved|available)\s+(from|at)\b.*$/i, "").trim();
      if (site) ref.source = stripTrailingPunct(site.split(/\.\s+/)[0]);
    } else {
      ref.authors = fixAuthors(beforeDate);
      const isReport = reportNo || /\breport\b/i.test(after);
      // 3) Journal article — locate "…, Vol(Issue), pages/Article №" ANYWHERE in `after` (titles may
      //    contain periods), then split title / journal at the LAST ". " before the volume.
      const jm = after.match(/,\s*(\d{1,4})\s*(?:\(([^)]{1,24})\))?\s*(?:,\s*((?:[A-Za-z]?\d+(?:\s*[–-]\s*[A-Za-z]?\d+)?)|e\d+|(?:[Aa]rticle\s+\S+)))?/);
      const journalLike = jm && jm.index > 0 && !isReport &&
        (jm[2] || jm[3] || after.slice(0, jm.index).includes(". "));
      if (journalLike) {
        const before = collapse(after.slice(0, jm.index)); // "Title. Journal Name"
        const cut = before.lastIndexOf(". ");
        if (cut > 0) {
          ref.title = stripTrailingPunct(before.slice(0, cut));
          ref.source = stripTrailingPunct(before.slice(cut + 2));
        } else {
          ref.title = stripTrailingPunct(before);
        }
        ref.source_type = "journal";
        ref.volume = jm[1] || "";
        ref.issue = (jm[2] || "").trim();
        ref.pages = (jm[3] || "").replace(/\s*[–-]\s*/, "–").replace(/^[Aa]rticle\s+/, "").trim();
      } else {
        // Non-journal: title = first sentence; remainder = source / publisher info.
        const parts = after.split(/\.\s+/);
        ref.title = stripTrailingPunct(collapse(parts.shift() || ""));
        const rest = collapse(parts.join(". "));
        if (isReport) {
          // 4) Report / organization report — report number stays separate from the publisher.
          ref.source_type = "report";
          if (reportNo) ref.report_number = reportNo;
          if (rest) ref.publisher = stripTrailingPunct(rest);
        } else if (ref.url && !rest) {
          // 5) Webpage: title + URL only (no publisher-like trailing text)
          ref.source_type = "website";
        } else if (ref.url) {
          ref.source_type = "website";
          ref.source = stripTrailingPunct(rest);
        } else if (rest) {
          // 6) Org-authored titled work with a distinct publisher -> report; otherwise a book.
          ref.source_type = looksLikeOrg(ref.authors) ? "report" : "book";
          ref.publisher = stripTrailingPunct(rest);
        } else {
          ref.source_type = "book";
        }
      }
    }
  } else {
    // Couldn't find a year in parentheses — low confidence.
    flags.needsReview = true;
    const parts = work.split(/\.\s+/);
    ref.authors = fixAuthors(parts.shift() || "");
    ref.title = stripTrailingPunct(collapse(parts.shift() || ""));
    ref.source = stripTrailingPunct(collapse(parts.join(". ")));
    ref.source_type = detectSourceType(s, ref);
  }

  // Flag incomplete / uncertain references.
  if (!ref.year || !ref.title || !ref.authors) flags.needsReview = true;
  if (ref.source_type === "journal" && !ref.source && !ref.doi && !ref.url) flags.needsReview = true;
  if (ref.source_type === "chapter" && (!ref.container || !ref.editors)) flags.needsReview = true;

  // Strong-conflict guards: if a block still carries fragments of a neighbouring reference
  // (a stray year/DOI/URL landed in the wrong field), surface it for review instead of silently
  // importing a mixed citation.
  const yearInField = /\((?:n\.?\s*d\.?|(?:1[6-9]|20)\d{2}[a-z]?)(?:,[^)]*)?\)/i;
  if (yearInField.test(ref.title || "") || yearInField.test(ref.source || "")) flags.needsReview = true;
  if (/https?:\/\/|\b10\.\d{4,9}\//i.test(ref.title || "")) flags.needsReview = true;
  if (/\b(?:1[6-9]|20)\d{2}\b/.test(ref.authors || "") || /https?:\/\//i.test(ref.authors || "")) flags.needsReview = true;

  ref._needsReview = flags.needsReview;
  ref._raw = collapse(raw);
  return ref;
}

// Public: parse a plain-text reference list (from paste, .docx, or .pdf) into
// structured references + a "reference section found" flag.
export function parseReferenceText(text) {
  if (!collapse(text)) return { references: [], sectionFound: false };
  const { section, sectionFound } = isolateReferenceSection(text);
  const chunks = splitReferences(section)
    // Drop obvious non-references (too short or heading-like leftovers).
    .filter((c) => c.length >= 12 && /[A-Za-z]/.test(c) && !REF_HEADINGS.test(c));
  const references = chunks.map(parseFreeform).filter((r) => r.title || r.authors || r.doi);
  return { references, sectionFound };
}

// Public: parse a .docx array buffer into structured references + section flag.
export async function parseDocx(arrayBuffer) {
  const text = await extractDocxText(arrayBuffer);
  return parseReferenceText(text);
}

// Duplicate key: DOI, else PMID, else normalized title+firstAuthor+year.
export function dedupKey(ref) {
  const doi = (ref.doi || "").trim().toLowerCase()
    .replace(/^doi:\s*/i, "").replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
  if (doi) return `doi:${doi}`;
  if (ref._pmid) return `pmid:${ref._pmid}`;
  const title = (ref.title || "").trim().toLowerCase().replace(/\s+/g, " ");
  const firstAuthor = (ref.authors_raw || ref.authors || "").split(/[;,]/)[0].trim().toLowerCase();
  const year = (ref.year || "").trim();
  if (!title) return "";
  return `t:${title}|a:${firstAuthor}|y:${year}`;
}
